import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Gallery.css';
import { useTheme } from '../contexts/ThemeContext';
import galleryData from '../data/gallery.json';
import { useContent } from '../contexts/ContentContext';

type GalleryItem = {
  id: number;
  title: string;
  description: string;
  category: string;
  image: string;
  featured: boolean;
};

const Gallery = () => {
  const { getSection } = useContent();
  const data = getSection<typeof galleryData>('gallery', galleryData);
  const [featuredItems, setFeaturedItems] = useState<GalleryItem[]>([]);
  const [selectedImage, setSelectedImage] = useState<GalleryItem | null>(null);
  const { } = useTheme(); // eslint-disable-line no-empty-pattern
  const navigate = useNavigate();

  useEffect(() => {
    // Get only the first 3 featured items
    const featured = data.gallery.filter(item => item.featured).slice(0, 3);
    setFeaturedItems(featured);
  }, [data]);

  const nextImage = () => {
    const currentIndex = featuredItems.findIndex(item => item.id === selectedImage.id);
    const nextIndex = (currentIndex + 1) % featuredItems.length;
    setSelectedImage(featuredItems[nextIndex]);
  };

  const prevImage = () => {
    const currentIndex = featuredItems.findIndex(item => item.id === selectedImage.id);
    const prevIndex = (currentIndex - 1 + featuredItems.length) % featuredItems.length;
    setSelectedImage(featuredItems[prevIndex]);
  };

  const openLightbox = (item: GalleryItem) => {
    setSelectedImage(item);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const handleViewFullGallery = () => {
    navigate('/gallery');
  };

  return (
    <section id="gallery" className="gallery-section">
      <div className="gallery-container">
        {/* Section Header */}
        <div className="gallery-header">
          <h2 className="gallery-title">
            <span className="title-accent">Our</span> Gallery
          </h2>
          <p className="gallery-subtitle">
            Capturing moments of innovation, learning, and collaboration at ASIMO Technical Club
          </p>
          <div className="gallery-actions">
            <button className="btn-view-full-gallery" onClick={handleViewFullGallery}>
              View Full Gallery
            </button>
          </div>
        </div>

        {/* Featured Gallery Grid */}
        <div className="featured-gallery-grid">
          {featuredItems.map(item => (
            <div 
              key={item.id} 
              className="gallery-item featured"
              onClick={() => openLightbox(item)}
            >
              <div className="gallery-card">
                <div className="image-container">
                  <img 
                    src={`/assets/images/gallery/main page/${item.image.split('/').pop()}`}
                    alt={item.title}
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.src = `/assets/images/gallery/gallery extended/${item.image.split('/').pop()}`;
                    }}
                  />
                  <div className="image-overlay">
                    <div className="overlay-content">
                      <h3 className="image-title">{item.title}</h3>
                      <p className="image-description">{item.description}</p>
                      <span className="image-category">{item.category}</span>
                    </div>
                  </div>
                </div>
                <div className="featured-badge">
                  <span>★ Featured</span>
                </div>
              </div>
            </div>
          ))}
        </div>

      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div className="lightbox-container" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox}>
              <span>×</span>
            </button>
            
            <button className="lightbox-nav prev" onClick={prevImage}>
              <span>‹</span>
            </button>
            
            <div className="lightbox-content">
              <img 
                src={`/assets/images/gallery/main page/${selectedImage.image.split('/').pop()}`}
                alt={selectedImage.title}
                onError={(e) => {
                  e.currentTarget.src = `/assets/images/gallery/gallery extended/${selectedImage.image.split('/').pop()}`;
                }}
              />
              <div className="lightbox-info">
                <h3>{selectedImage.title}</h3>
                <p>{selectedImage.description}</p>
                <span className="lightbox-category">{selectedImage.category}</span>
              </div>
            </div>
            
            <button className="lightbox-nav next" onClick={nextImage}>
              <span>›</span>
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;